import {
    Background,
    BackgroundColor,
    Container,
    Spacer,
    SpacerVariant,
    backgroundOptions,
    spacerOptions,
} from "../../components/layout"
import { Color, TypographyColor } from "../../components/typography"
import { Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../../Typography"

import React from "react"
import { useCheckForEmptyText } from "../../../hooks/use-check-for-empty-text"

interface ThreeColumnImageListProps {
    background: { color: BackgroundColor } | BackgroundColor
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    headline: TypographyElement[]
}

const ThreeColumnImageList: types.Brick<ThreeColumnImageListProps> = ({
    background,
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
    headline,
}) => {
    const bgColor = typeof background === "object" ? background.color : background

    const emptyHeadline = useCheckForEmptyText(headline)

    return (
        <Background color={bgColor}>
            <Container>
                <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
                <Color
                    color={
                        bgColor === BackgroundColor.Green || bgColor === BackgroundColor.DarkBlumine
                            ? TypographyColor.White
                            : TypographyColor.Darkgrey
                    }
                >
                    {!emptyHeadline && (
                        <Typography
                            allowList={["h3", "h4", "small", "sup", "link", "bold", "checkList"]}
                            propName="headline"
                        />
                    )}
                    <ul className="mt-5 grid grid-cols-1 gap-x-5 gap-y-3 sm:mt-7 sm:grid-cols-3 sm:gap-y-7 md:gap-x-7 md:gap-y-10 lg:mt-10 lg:gap-x-5 xl:gap-x-16 xl:gap-y-16">
                        <Repeater propName="repeater" />
                    </ul>
                </Color>
                <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
            </Container>
        </Background>
    )
}

ThreeColumnImageList.schema = {
    name: "ThreeColumnImageList",
    label: "Small Image Text (3 Col)",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/3ColumnImageList.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "ThreeColumnImageListItem",
                    label: "Small Image Text Item",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: backgroundOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): ThreeColumnImageListProps => ({
        background: { color: BackgroundColor.White },
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Small,
        variantBottom: SpacerVariant.Small,
        headline: [
            {
                type: "h4",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                        bold: true,
                    },
                ],
            },
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc placerat sagittis faucibus.",
                    },
                ],
            },
        ],
    }),
}

export default ThreeColumnImageList
